import {
  inviteAccountSchema,
  type AdminEvent,
  type EventRole,
  type InviteAccount,
} from '@growup/site-shared';
import { Autocomplete, Button, Checkbox, Field, Notice, Select, Textarea } from '@growup/ui';
import { useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatEventDay, ROLE_LABEL } from './labels';

const searchAccounts = (q: string) =>
  getJson(
    `/api/site/events/invite-accounts?q=${encodeURIComponent(q)}`,
    z.array(inviteAccountSchema),
  );

/**
 * Convidar uma conta que ja existe para o roster (pedido pelo GrowuP a
 * 29-10-2026). A pessoa recebe um email e aceita ou recusa na pagina do
 * evento; num jovem, o email vai para o encarregado. Ate aceitar nao ocupa
 * lugar.
 */
export function InviteForm({
  event,
  onInvited,
  onClose,
}: {
  event: AdminEvent;
  onInvited: () => void;
  onClose: () => void;
}) {
  const [login, setLogin] = useState('');
  const [account, setAccount] = useState<InviteAccount | null>(null);
  const [role, setRole] = useState<EventRole>('volunteer');
  const allDays = event.days.map((d) => d.day);
  const [days, setDays] = useState<string[]>(allDays);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const roles: EventRole[] = account?.isYouth
    ? ['volunteer']
    : (['visitor', 'volunteer', 'streamer'] as const).filter(
        (r) =>
          (r !== 'volunteer' || event.volunteersWanted) &&
          (r !== 'streamer' || event.streamersWanted),
      );

  const invite = async () => {
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      await send('POST', `/api/site/events/${event.id}/invitations`, {
        accountId: account.id,
        role,
        days: days.length === allDays.length ? null : days,
        note,
      });
      setDone(
        account.isYouth
          ? `Convite enviado ao encarregado de ${account.login}.`
          : `Convite enviado a ${account.login}.`,
      );
      setAccount(null);
      setLogin('');
      setNote('');
      onInvited();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Convidar para o roster"
      className="flex flex-col gap-3 border border-gu-line p-4"
    >
      <p className="text-caption text-gu-ink-muted">
        Convide alguém que já tem conta no site. Recebe um email e aceita ou recusa na página do
        evento; num jovem, o email vai para o encarregado. Até aceitar, não ocupa lugar.
      </p>
      <Field label="Conta" hint="Escreva parte do login e escolha a pessoa.">
        <Autocomplete
          className="max-w-md"
          value={login}
          onChange={(v) => {
            setLogin(v);
            setAccount(null);
            setDone(null);
            setError(null);
          }}
          search={searchAccounts}
          getKey={(a) => a.id}
          renderItem={(a) => (
            <span>
              {a.login}
              {a.isYouth ? <span className="text-caption text-gu-ink-muted"> · jovem</span> : null}
            </span>
          )}
          onSelect={(a) => {
            setAccount(a);
            setLogin(a.login);
            if (a.isYouth) setRole('volunteer');
          }}
        />
      </Field>
      {account ? (
        <>
          <Field
            label="Papel"
            hint={account.isYouth ? 'Um jovem só vai como voluntário.' : undefined}
          >
            <Select
              className="max-w-xs"
              value={role}
              onChange={(e) => setRole(e.target.value as EventRole)}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </Field>
          {allDays.length > 1 ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 font-semibold">Dias</legend>
              <div className="flex flex-wrap gap-3">
                {allDays.map((d) => (
                  <Checkbox
                    key={d}
                    label={formatEventDay(d)}
                    checked={days.includes(d)}
                    onChange={(e) =>
                      setDays(
                        e.target.checked
                          ? allDays.filter((x) => x === d || days.includes(x))
                          : days.filter((x) => x !== d),
                      )
                    }
                  />
                ))}
              </div>
            </fieldset>
          ) : null}
          <Field label="Mensagem no email" optional>
            <Textarea
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {done ? <Notice tone="success">{done}</Notice> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={busy || !account || days.length === 0}
          onClick={() => void invite()}
        >
          Enviar convite
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </section>
  );
}
